// Module 03: The Oscillating Narrative Framework
// The Art of Story - Learning Module
// Draws on Marshall Duke's three family-story patterns (as he described them to Bruce Feiler, 2013)
// and the "Do You Know?" findings (Duke, Lazarus & Fivush, 2008), which are correlations

import { CRISIS_HELP } from '../crisisSupport.js';

export const storyModule03Content = {
  id: 'oscillating-narratives',
  title: 'Stories That Hold Struggle and Strength',
  subtitle: 'Oscillating narratives: the hard parts and your response, told together',
  description: 'Learn the three shapes a story about difficulty can take (ascending, descending or oscillating), what the research on them does and doesn\'t show, and how to tell the hard parts of your life honestly without letting them have the last word.',
  duration: '40 minutes',
  image: '/images/mod-03-oscillating.png',

  content: {
    overview: `Picture two brothers telling their family's immigration story, one after the other. They're an illustrative pair, but you've probably met both of them.

The older brother, Marcus, went first: "Our parents came here with nothing. They worked themselves to death for minimum wage. We grew up poor, always struggling, never quite catching up. That's just how life is for people like us: you fight and fight and it's never enough."

The younger brother, James, shifted uncomfortably. When it was his turn, he told the same story completely differently: "Mum and Dad came here with nothing, which was terrifying for them. They worked brutal jobs: factory work, cleaning offices at night. Money was tight. But they figured it out. We didn't have much, but we had each other. They taught us that when things get hard, you find a way. We're still figuring out our own paths, but we know how to handle difficulty because we watched them do it."

Same family. Same events. Same childhood poverty.

But Marcus heard a descending narrative: "We struggled and we're still stuck." James heard an oscillating narrative: "We struggled AND we found ways through it."

It's easy to picture how each of them meets a setback. Marcus braces, defensive, waiting for the next thing to go wrong. James is uncertain but willing to try again. From one scene we can't tell which came first, the story or the temperament, but each brother is rehearsing a different lesson about hard times.

**Psychologists have looked closely at stories like these.** Marshall Duke and Robyn Fivush spent years at Emory University studying how families talk about themselves. Duke describes three patterns that family stories fall into, and the oscillating one (struggle *and* strength, both named) is the one he considers most healthful for children. What they measured is narrower: children who know more of their family's history tend to report higher self-esteem and a stronger sense of control over their own lives. We'll get to how strong that evidence actually is later in the module; the short version is that it's real, it's correlational, it's about knowing family stories rather than any one story shape, and the mechanism probably isn't what most people assume.

We think the way you tell your stories does more than describe your life. It's the version of events you rehearse, and the version you pass on to the people who hear you.

This module is about understanding the three fundamental narrative patterns (ascending, descending and oscillating) and learning to tell stories that hold both the struggle and the strength, for yourself and for the people who hear them.

Because story structure isn't just about entertainment. It's about how we make sense of difficulty, how we find meaning in struggle, and how we actually survive hard things.`,

    learningObjectives: [
      'Identify the three fundamental narrative patterns (ascending, descending, oscillating) in your own stories and the stories around you',
      'Understand what Marshall Duke and Robyn Fivush\'s research on family stories found, and what it didn\'t test',
      'Recognise why oscillating narratives can help you hold difficulty without being defined by it, and where the evidence stops',
      'Transform descending narratives into oscillating ones without diminishing the reality of struggle',
      'Craft oscillating narratives for different contexts (personal reflection, professional settings, family storytelling)',
    ],

    sections: [
      {
        id: 'three-stories',
        title: 'The Three Stories We Tell',
        duration: '9 minutes',
        content: {
          intro: `Imagine a woman telling the story of her grandmother's death three different ways in the same week, depending on who asks.

To her boss: "My grandmother passed. She had a good life. I'm fine, thanks."

To a close friend: "Losing my grandmother has been harder than I expected. We were really close. I'm struggling."

To her therapist: "My grandmother died, which broke my heart. I cried for days. But as I've been going through her things, I'm finding all these letters she wrote me over the years. Reading them, I can hear her voice. The grief is real, and I don't think it's going away anytime soon. But I'm also feeling connected to her in a way I didn't expect. It's strange. I'm sad and I'm grateful at the same time."

Three versions. Same event. Completely different narrative structures.

The first is ascending (minimising difficulty). The second is descending (stuck in pain). The third is oscillating (holding both difficulty and meaning).

Marshall Duke's interest began with an observation by his wife, Sara, a psychologist who works with children with learning disabilities: the children who knew a lot about their families seemed to cope better. Duke and his Emory colleague Robyn Fivush set out to test it, and later recorded families talking over dinner.

The three patterns come from family stories, but we find them useful for any story you tell about yourself.`,

          mainContent: `**The Three Narrative Patterns**

Let's take one life event, losing a job, and tell it three different ways:

**1. The Ascending Narrative: "Everything Gets Better"**

"I lost my job, but honestly, it was the best thing that ever happened. Within two weeks, I had three interviews. Within a month, I landed an even better position with higher pay and a corner office. It's all worked out perfectly."

**Structure:** Things start OK, something happens, and then everything gets progressively better.

**Family version:** "Our family has always been successful. Your great-grandfather came here with nothing and built a business. Your grandfather expanded it. Your father doubled it. Now it's your turn to take it even further."

**Why it's appealing:** Optimistic, motivating, feels good to tell.

**The problem:** when reality inevitably includes setbacks, you have no framework for making sense of them. To be fair to the evidence, this is an argument rather than a measured finding. McAdams' research documents what *redemption* and *contamination* sequences predict, not what a relentlessly upward story does to you. But the logic is hard to dodge: a story with no difficulty in it has nowhere to put difficulty when it arrives.

---

**2. The Descending Narrative: "Everything Gets Worse"**

"I lost my job and it's been a disaster ever since. I can't find anything. My savings are running out. I don't know what I'm going to do. Everything I touch seems to fall apart."

**Structure:** Things start OK (or even good), something happens, and then everything gets progressively worse.

**Family version:** "Our family has always struggled. Your great-grandparents lost everything in the Depression. Your grandparents worked themselves to the bone and barely made it. Your parents tried, but life is hard. Don't expect too much."

**Why it happens:** Sometimes it's the truth of your experience. Sometimes it's a way of processing genuine difficulty.

**The problem:** McAdams and colleagues (2001) found that midlife adults whose life stories run heavily to *contamination sequences* (good things reliably turning bad) report more depression and lower well-being. That's a correlation, and the arrow probably runs both ways: a low mood makes the descending story easier to tell, and telling it can keep the mood low. Either way you're rehearsing the same conclusion, which is that nothing you do changes the outcome.

---

**3. The Oscillating Narrative: "We've Had Ups and Downs"**

"I lost my job, which was genuinely frightening: I had a mortgage and kids in school. The first month was rough. I had to swallow my pride and ask for help. But I used the time to reassess what I actually wanted. I took a contract role that paid less but gave me flexibility. It was hard, and there were moments I doubted myself. But I learned I could adapt. I'm in a different place now. Not perfect, but more resilient than I was before."

**Structure:** Things go up and down. There's struggle AND there's strength. Difficulty is acknowledged, and growth is real but not absolute.

**Family version:** "Our family has been through a lot. Your great-grandparents emigrated and struggled at first, but they built a community. Your grandparents faced the Depression and figured out how to help each other survive. Your parents had setbacks, and they learnt from them. We're not perfect, but we know how to get through hard things together."

**Why it's powerful:** It's honest. It doesn't deny difficulty, but it doesn't end there either.

**Where this comes from:** the three-pattern typology is Marshall Duke's, as he described it to Bruce Feiler for the 2013 *New York Times* piece "The Stories That Bind Us." Duke called the oscillating pattern "the most healthful narrative." The measured outcomes (self-esteem, a sense of control over one's own life, lower anxiety) come from the separate "Do You Know?" work below. That work measured how much children knew about their family's history, not which pattern their family's stories took, and its results are correlations rather than proof that the stories caused anything. It's a good idea with real evidence nearby and honest limits on that evidence. Both things are true.

[RESEARCH: The "Do You Know?" Scale | Marshall Duke, Amber Lazarus and Robyn Fivush built a 20-question scale asking children things they couldn't have witnessed: where their grandparents grew up, how their parents met, what their parents learnt from good or bad experiences. Children who could answer more of them had higher self-esteem, a stronger sense of control over their own lives, and lower anxiety (Duke, Lazarus & Fivush, 2008, *Psychotherapy: Theory, Research, Practice, Training*). **Read the finding carefully, because it is routinely oversold.** This is a correlation in a modest sample, and Duke has been explicit that knowing the facts isn't what does the work: a family where these stories get told is usually a family that eats together, talks, and holds together under strain. The scale is measuring that. Which is why the next block matters more than this one.]

[RESEARCH: The Act of Storytelling | Robyn Fivush's later work points to the process more than the facts: families who talked about hard family events in more coherent, emotionally open ways had children who coped better over the next two years. It's a link, not proof, but it suggests that telling a story *with* someone (listening, asking questions, filling in details together) matters more than the stored facts. So when you tell a family story with someone rather than just *to* them, you're doing the part that seems to matter.]

[INSIGHT: Why Oscillating Matters | Oscillating narratives try to do something important: fit difficulty into your story without letting it define you. They say, "Yes, hard things happened. AND I'm more than what happened to me." Holding both (the struggle is real, AND I have agency) is why we think the pattern is worth practising. It's not toxic positivity ("everything's great!") and it's not hopelessness ("everything's terrible"). It's honest complexity.]

**Which Pattern Do You Default To?**

Most people have a default narrative pattern they use unconsciously. Some people are chronic ascenders ("Everything always works out!"). Some are chronic descenders ("Nothing ever goes my way"). Some oscillate naturally.

None of these is moral. They're patterns you've learnt, often from the stories you heard growing up. But they have consequences.`,

          practicalApplication: `**Quick Self-Assessment: Your Narrative Default**

Think of a recent difficulty or setback (nothing too intense: maybe a project that didn't go well, a conflict with a friend, a disappointment).

Now tell that story to yourself in 3-4 sentences. Write it down if you can.

Read what you wrote. Which pattern did you use?

- **Ascending:** Did you skip over the difficulty to focus mainly on the positive outcome or silver lining?
- **Descending:** Did you focus primarily on what went wrong, with little mention of how you responded or what you learnt?
- **Oscillating:** Did you acknowledge both the genuine difficulty AND your response to it, including any growth or learning?

There's no judgement here, just awareness. Most of us have a default pattern. Recognising yours is the first step to choosing differently when needed.`,
        },
      },

      {
        id: 'resilience-research',
        title: 'Why This Matters: The Research and Its Limits',
        duration: '8 minutes',
        content: {
          intro: `Picture a teenager coming home from school absolutely gutted. She's failed to make the netball team. "I'm terrible at sports," she says. "I'm not athletic. I'm never going to be good at this stuff."

Classic descending narrative. One setback becomes total identity.

Her mum could go ascending: "You're great! You'll make it next year! Everything will be fine!"

Say she doesn't. Instead, she says: "Not making the team sucks. I know you're disappointed. I am too. I saw how hard you trained. I also know you showed up to every training session even when you didn't want to. That's resilience. You're learning what it feels like to really want something and not get it. That's a tough lesson, but it's an important one. You don't have to be fine right now. And you get to decide if you want to try again or try something else. Either way, you learnt something about yourself."

Oscillating. Acknowledges pain. Locates agency. Stays honest.

Now imagine that two weeks later she tries out for the school play and gets a small part. "I'm not giving up on netball," she tells her mum, "but I wanted to try something where I could actually improve."

That's what an oscillating story tries to do. It doesn't erase the difficulty. It leaves room for the idea that difficulty doesn't erase you.

Why might this help? The rest of this section looks at the psychology of telling (or hearing) a story in this shape, and at where the evidence runs out.`,

          mainContent: `**Why Oscillating Narratives Might Help**

**1. Integrating Difficulty Without Being Defined By It**

When you tell a purely descending narrative ("I was bullied and it destroyed me"), the event and your identity can start to feel fused. You *are* the person who was destroyed by bullying.

When you tell a purely ascending narrative that skips over difficulty ("It was rough, but I'm fine now!"), the event gets pushed away rather than worked through. You haven't processed it; you've bypassed it.

Oscillating narratives do neither. They say: "That happened to me. It was hard. I responded. I'm changed, but not destroyed."

[RESEARCH: Narrative Processing and Identity | In Dan McAdams's narrative identity theory, the life story you carry is a central part of who you are, alongside your traits and goals. Baerger and McAdams (1999) found that adults who told their life stories more coherently reported less depression and more life satisfaction (a correlation). We think oscillating narratives help with coherence, because they fit difficulty and agency into one story instead of leaving the hard parts out.]

**2. Modelling Agency in the Face of Difficulty**

Descending narratives emphasise external forces: "This happened *to* me."

Oscillating narratives include agency: "This happened *to* me, AND this is what I did with it."

Even when outcomes aren't perfect, oscillating narratives foreground response. You may not have chosen what happened, but you chose (or are choosing) how to respond.

This is the part with the best evidence behind it. In Jonathan Adler's psychotherapy study (Module 1), the narratives that gained *agency* (where the teller moved from passenger to author) were followed by improvements in mental health, in that order. In that study, the story changed, then the person did.

[INSIGHT: Agency Isn't About Blame | Emphasising your agency in a story doesn't mean blaming yourself for what happened. You didn't cause the difficulty. But oscillating narratives locate power in your response, not just in the event. That moves your sense of control from "the world happens to me" to "I have some influence over what I do with what happens."]

**3. Creating a Framework for Future Difficulty**

Oscillating stories may also help with what hasn't happened yet: they offer a way of thinking about difficulty before it arrives.

When a child hears: "Great-grandma lost her farm in the Depression. It was devastating. She cried for weeks. Then she moved to the city, learnt to sew, and started a tailoring business. She never got the farm back, but she built a different kind of life," that child has heard one way to face difficulty.

The lesson isn't "bad things don't happen" (ascending) or "bad things happen and destroy you" (descending). It's "bad things happen, they hurt, people struggle, AND people figure out ways forward."

[RESEARCH: Family Stories Across Generations | Robyn Fivush's research suggests teenagers' own life stories are partly shaped by the stories they hear about their parents' childhoods, and those who tell these family stories with more connection and perspective-taking report higher well-being (Fivush, Bohanek & Zaman, 2011). Stories that include both the struggle and the response may give children a template: "This is how people in my family handle hard things."]

**4. Building Emotional Range and Regulation**

Oscillating narratives require holding two truths simultaneously:
- "This was genuinely hard" AND "I found a way forward"
- "I was hurt" AND "I grew"
- "I have regrets" AND "I made the best choice I could at the time"

This capacity to hold two truths at once is sometimes called **dialectical thinking**, and it's a skill worth practising.

In James Pennebaker's expressive-writing studies, people write about a difficult experience for 15 to 20 minutes on three or four days. Writers have made fewer health-service visits and shown some changes in immune measures (Pennebaker, Kiecolt-Glaser & Glaser, 1988), though across 146 studies the average benefit is small (Frattaroli, 2006).

What seems to matter is not just telling the story but making sense of it, holding **both pain and meaning**. There's more on that later in this module.

[COMPARE: Descending | "I was made redundant at 52. No one wants to hire someone my age. I'm basically unemployable now. My career is over." | Oscillating | "I was made redundant at 52, which was a genuine blow to my confidence and my financial security. The first few months were rough. I won't pretend otherwise. I've had to rethink what 'career' means at this stage of life. I'm doing contract work now, which isn't what I imagined, but I have more control over my time. I'm still figuring it out, and it's uncomfortable. But I'm not done yet."]

**Why "Positive Reframing" Isn't the Same**

You might be thinking: "Isn't this just 'look on the bright side'?"

No. Oscillating narratives are different from toxic positivity in crucial ways:

- **They don't deny pain:** "It was hard" is said clearly, not glossed over
- **They don't promise easy resolution:** "I'm still figuring it out" is honest
- **They acknowledge ongoing struggle:** "It's uncomfortable" doesn't disappear
- **They locate agency, not just outcome:** "I'm doing X" matters more than "Everything's great now"

James Gross's emotion-regulation research draws the distinction that matters here: **suppression** (pushing the feeling down and carrying on) versus **reappraisal** (changing what the situation means to you). Suppression is the expensive one: it costs cognitive effort, it doesn't reduce the feeling much, and it leaves the person doing it less connected to whoever they're with. Reappraisal tends to cost less and go with better well-being and relationships (Gross & John, 2003; Butler et al., 2003). An oscillating narrative is reappraisal with a shape to it.`,

          practicalApplication: `**Recognising Oscillating Narratives in Others**

This week, pay attention to the stories you hear at work, in conversation and in the media.

When someone tells a story about difficulty:
- Do they use a descending pattern? (focus on what went wrong, little agency)
- Do they use an ascending pattern? (skip past difficulty to the resolution)
- Do they use an oscillating pattern? (acknowledge difficulty AND response)

Notice how each pattern makes you feel as a listener.
Notice which stories stick with you.
Notice which stories make you think, "I could face something like that."

You're developing your ear for narrative structure. That's the foundation for constructing oscillating narratives yourself.`,
        },
      },

      {
        id: 'crafting-oscillating',
        title: 'Crafting Your Oscillating Stories',
        duration: '9 minutes',
        content: {
          intro: `Imagine someone trying an oscillating narrative for the first time. She's been telling a descending story about her divorce for three years: "My marriage ended and I've never really recovered. I'm terrible at relationships. I'll probably be alone forever."

After learning about oscillating narratives, she tries again: "My marriage ended, which was devastating. It took me two years to feel like myself again. I'm still figuring out what I want in a relationship. But I'm learning what I need and what I won't compromise on. I'm not the same person I was before, and that's not entirely bad."

She stops. Her hands are shaking. "That felt weird," she says. "Like I was lying."

"Did you lie?" a friend asks. "Was any part of that untrue?"

She thinks about it. "No. It's all true. But I've been telling the other version (the 'I'll be alone forever' version) for so long that this one feels fake, even though it's more accurate."

That's the challenge. Understanding oscillating narratives intellectually is one thing. Actually crafting them is another, especially when you've defaulted to descending or ascending patterns for years.

This section gives you the structure and language patterns that make oscillating narratives work. Fair warning: the first few times you try this, it might feel uncomfortable. That's normal. You're not lying. You're just telling a fuller version of the truth.`,

          mainContent: `**The Oscillating Narrative Structure**

At its core, an oscillating narrative follows this pattern:

1. **Context:** What was the situation before difficulty struck?
2. **Difficulty:** What happened? What was genuinely hard about it?
3. **Struggle:** How did you experience it? (Acknowledge real pain/fear/confusion)
4. **Response:** What did you do? (Even small actions matter)
5. **Outcome:** Where are you now? (Honest, not necessarily "perfect")
6. **Meaning:** What did you learn or how did you change? (Growth without minimising)

**Example: Job Loss (Oscillating Structure)**

1. **Context:** "I'd been at the same company for 12 years. It was stable and predictable. I knew how everything worked."

2. **Difficulty:** "When they restructured and made my role redundant, it wasn't just about money. My identity was wrapped up in that job."

3. **Struggle:** "The first month was brutal. I couldn't bring myself to update my CV. I felt like a failure at 45. I avoided social situations because I didn't want to answer, 'What do you do?'"

4. **Response:** "Eventually, I reached out to a former colleague who'd been through something similar. She reminded me that I had skills, not just a job title. I started applying, badly at first. I took a few contract roles that weren't ideal, but they got me moving again."

5. **Outcome:** "I'm in a different place now: contract work instead of permanent, which has its own instability. But I have more control over the kind of work I take on. It's not what I expected, but it's not terrible either."

6. **Meaning:** "I learnt that my worth isn't tied to one employer. I also learnt I'm more adaptable than I thought, even when I don't feel ready."

**The Language of Oscillating Narratives**

Notice the specific linguistic patterns that create oscillation:

**"It was hard AND I found a way"**
Not "It was hard BUT I found a way" (minimises the difficulty)
AND holds both truths simultaneously.

**"I struggled AND I'm still here"**
Acknowledges ongoing difficulty without catastrophising.

**"I don't have it figured out AND I'm learning"**
Honest about uncertainty whilst affirming agency.

**"I was hurt AND I grew"**
Both are true. Neither cancels the other out.

[INSIGHT: The Power of "AND" | The word "and" is the grammatical heart of oscillating narratives. "But" creates opposition: one part of the sentence tries to override the other. "And" creates integration: both parts are equally true. Swapping "but" for "and" is a common tip in dialectical-thinking skills; you'll find it in self-help guides for dialectical behaviour therapy (DBT), for example. It's a practice tip, not a tested finding: try it on a sentence about your own life and judge it on what it does to the sentence.]

**Common Oscillating Narrative Mistakes**

**Mistake 1: Rushing to the Resolution**

❌ "I was bullied in school, which was hard, but now I'm totally fine and I even thank them for making me stronger!"

This is actually an ascending narrative disguised as oscillating. The difficulty is mentioned but not genuinely felt.

✅ "I was bullied in school, which left scars I'm still aware of. I don't thank them. That's absurd. But I did develop empathy for people who feel excluded, and that's shaped the kind of person I try to be."

**Mistake 2: False Balance**

❌ "My father was an alcoholic, which was terrible. But he also taught me to ride a bike!"

Not all oscillation is meaningful. The "positive" needs to be genuinely connected to growth, not just a random good thing pasted on.

✅ "My father was an alcoholic, which created chaos in our home. I learnt to read situations quickly: when was it safe, when wasn't it? That hyper-vigilance cost me in some ways. But it also made me perceptive. I notice things about people that others miss."

**Mistake 3: Over-Explaining the Meaning**

❌ "I lost my job, which was hard, but then I realised that the universe was redirecting me to my true purpose and everything happens for a reason and now I'm exactly where I'm meant to be."

Oscillating narratives don't require cosmic justification. Sometimes meaning is small and quiet.

✅ "I lost my job, which was hard. I'm in a different field now. I don't know if it's 'meant to be,' but I'm learning new things. That matters."

[RESEARCH: Meaning-Making and Maturity | Kate McLean studies how narrative identity develops, a process that runs through adolescence into early adulthood. In one longitudinal study, 23-year-olds who drew more sophisticated meaning from the turning points in their lives scored higher on a measure of identity maturity, and on optimism (McLean & Pratt, 2006). "Oscillating" is our word, not hers, but finding meaning in a hard turn without forcing a happy ending is close to what this module teaches.]

**Oscillating Narratives in Different Contexts**

The core structure remains the same, but how you tell an oscillating narrative shifts based on context:

**Professional context (networking, interviews):**
- Less emotional detail, more focus on response and learning
- "That project didn't go as planned, which was frustrating. I had to rethink our approach. We ended up with a different solution that's held up well since."

**Personal context (friends, therapy, journaling):**
- More emotional honesty, space for ongoing struggle
- "I'm still hurt by how that ended. I don't think I handled it well. But I'm trying to learn from it without beating myself up forever."

**Family context (talking with children):**
- Age-appropriate difficulty, clear modelling of response
- "Grandpa lost his job when I was young. Our family didn't have much money for a while. It was scary. But your grandparents figured it out. They helped each other and asked for help when they needed it. That's what people do when things are hard."

The context changes the details and emotional temperature, but the structure stays consistent: difficulty + response + honest outcome + meaning.`,

          practicalApplication: `**Exercise: Restructure a Descending Narrative**

Think of a story you tell about yourself that follows a descending pattern: something that happened that still feels primarily negative when you think about it.

Write it out briefly in its current form (3-4 sentences).

Now, without minimising the difficulty, rewrite it using the oscillating structure:

1. **Context:** What was life like before this happened?
2. **Difficulty:** What happened?
3. **Struggle:** What was genuinely hard about it?
4. **Response:** What did you do, even if it felt inadequate at the time?
5. **Outcome:** Where are you now with it?
6. **Meaning:** What did you learn, however small?

This isn't about "making it positive." It's about locating your agency alongside the difficulty.

Notice if the story feels different when told this way. Notice if *you* feel different when you tell it this way.`,
        },
      },

      {
        id: 'transformation-practice',
        title: 'From Descending to Oscillating: Transformation Practice',
        duration: '8 minutes',
        content: {
          intro: `Picture a man in his sixties who has been carrying the same story for more than forty years.

"I failed out of uni in my first year," he says. "Partied too much, didn't study, completely blew it. I tell myself I'm not academic, never have been. That was more than forty years ago, and I've been telling that story ever since."

Ask him how often it comes up.

"Constantly," he says. "Whenever I'm in a situation where I feel out of my depth intellectually (meetings, conversations, even reading), that story kicks in. 'You're not smart enough for this. Remember? You failed out of uni.'"

Ask whether anything has challenged that story in all that time.

He pauses. "I mean... I've run a successful business for thirty years. I've learnt complex systems, figured out problems, taught myself things. But that doesn't count, because it's not academic."

That's how descending narratives work. They groove themselves so deeply into your identity that you unconsciously filter out any evidence that contradicts them. You've been telling the story so long, it becomes the only story you can see.

Transforming them into oscillating narratives isn't a one-time rewrite. It's an ongoing practice of telling a fuller, more honest version of your life. This section shows you how.`,

          mainContent: `**The Stories That Get Stuck**

Certain stories tend to get stuck in descending patterns:

**Childhood wounds:**
"My parents divorced when I was 10 and I never felt secure after that."

**Relationship failures:**
"I was cheated on and I haven't been able to trust anyone since."

**Career setbacks:**
"I was passed over for that promotion and I've been stuck ever since."

**Shame-based narratives:**
"I failed at that business and I'm not cut out to be an entrepreneur."

These stories feel *true*, because they are. The difficulty happened. The pain was real.

But the *ending* of these stories ("and I never...," "and I can't...," "and I'm not...") is where the descending pattern locks in.

[RESEARCH: Narrative Foreclosure | The term belongs to psychologist **Mark Freeman**, who coined it in a 2000 book chapter and returned to it in a 2011 chapter in the book *Storying Later Life*. His definition is precise and bleak: the premature conviction that one's life story has effectively ended. In other words, no new chapters are coming. The 2011 chapter is about ageing, but the shape shows up at any age. "I was hurt and I'll never trust again" is a foreclosed story about a person who is still alive. Later researchers (Bohlmeijer and colleagues, 2011) proposed it as a "sensitizing concept": a lens for noticing something, not a proven predictor. So we won't claim it predicts depression. We'll say what it plainly is: a story that has been closed while the life is still open. Oscillating narratives keep the ending unwritten: "I was hurt, I struggle with trust, AND I'm learning."]

**The Transformation Process: Four Steps**

**Step 1: Acknowledge the Descending Version (Don't Bypass It)**

Before you can transform a descending narrative, you have to honour it. The pain in that story is real. Rushing to "reframe" it feels like gaslighting yourself.

Write out the descending version. Let it be what it is.

"I was made redundant at 52. I sent out 200 applications and got 3 interviews. I'm too old, too expensive, too outdated. My career is over."

Sit with that. It's how you've been holding this story. It deserves acknowledgment.

**Step 2: Separate Event from Identity**

The event happened. The pain was real. But the *meaning* you've assigned ("I'm too old," "My career is over") is interpretation, not fact.

Ask yourself:
- What happened? (the event)
- What did it mean to me? (the interpretation)
- Is that the only possible meaning? (opening space)

"What happened: I was made redundant at 52 and struggled to find work for months."

"What I made it mean: I'm unemployable. My career is over."

"Other possible meanings: The market is tough for my skill set right now. I haven't figured out how to position myself yet. I'm facing age discrimination, which is real and unfair, but not absolute."

[INSIGHT: Externalising the Problem | Narrative therapy (Michael White & David Epston) teaches "externalising": separating the person from the problem. You're not "unemployable." You're a person facing difficulty finding employment in a tough market with age bias. That shift creates space for agency. The problem is external (market, bias, timing), not inherent (your worth).]

**Step 3: Locate Small Responses (Even Failed Ones)**

Descending narratives erase agency by focussing only on outcomes. Oscillating narratives locate agency in *response*, regardless of outcome.

Even if you haven't "solved" the problem, what have you done?

"I sent out 200 applications" ← That's agency
"I got 3 interviews" ← You showed up
"I updated my CV four times trying to figure out the right angle" ← You adapted
"I reached out to former colleagues, even though it was awkward" ← You asked for help
"I took a contract role that felt beneath me to keep moving" ← You stayed in motion

Oscillating narratives count these responses. They matter.

**Step 4: Rewrite with Honest Complexity**

Now, write the oscillating version. Include:
- The genuine difficulty (don't minimise)
- Your responses (even imperfect ones)
- Where you are now (honest, not resolved)
- What you're learning (however small)

"I was made redundant at 52, which was a blow I wasn't prepared for. I've sent out over 200 applications and had 3 interviews. The market is brutal, and age bias is real. I've had to swallow my pride and take contract work that doesn't match my experience level. I'm frustrated and sometimes feel invisible. But I'm still showing up. I'm learning to position my experience differently. I'm figuring out how to adapt, even when I don't want to. I don't have this solved, but I haven't given up yet."

That's oscillating. It doesn't erase the pain. It doesn't promise resolution. But it includes your agency alongside the difficulty.

**When Oscillating Narratives Feel Like Lies**

Sometimes, when you first try to tell an oscillating version of a deeply descending story, it feels false.

"But I *don't* feel resilient. I *do* feel destroyed."

That's normal. You're not pretending the difficulty doesn't exist. You're adding the parts of the story you've been editing out: your responses, your ongoing efforts, the fact that you're still here.

The first time you write an oscillating version it may feel forced. Keep going: many people find it feels truer with practice.

[RESEARCH: Expressive Writing and Narrative Change | In Pennebaker's studies, people write about a painful experience for 15 to 20 minutes a day over three or four days. Those whose physical health improved most tended to use more insight and cause words ("I realise", "because") as the days went on (Pennebaker, Mayne & Francis, 1997), which suggests that turning the experience into a story matters. Early entries might be descending ("This happened and it destroyed me"). Later ones might bring in response and meaning ("This happened, it was terrible, and this is what I'm learning"). In our terms, that's a move from a descending story towards an oscillating one.]

**Go gently with this.** Writing about something painful usually brings a short-term rise in distress, so expect to feel worse for a while afterwards. You can stop at any time. If it gets too much, talk to someone you trust or a support line in your country. If you might be in danger or thinking about suicide, get help now: ${CRISIS_HELP}.

**Oscillating Doesn't Mean "Over It"**

One final clarification: Oscillating narratives don't require you to be "over" what happened.

You can still be angry.
You can still be grieving.
You can still wish it hadn't happened.

Oscillating narratives simply add: "AND I'm finding ways to live with this. AND I'm still here. AND I'm doing something, even if it's just surviving today."

That's enough. That's the oscillation.`,

          practicalApplication: `**Transformation Practice: The Four-Step Rewrite**

Choose one story you've been telling in a descending pattern, something that still feels stuck.

**Step 1:** Write the descending version (3-5 sentences). Let it be what it is. Don't judge yourself for how you've been holding this story.

**Step 2:** Separate event from identity. Write:
- What happened: (just the facts)
- What I made it mean: (the interpretation)
- Other possible meanings: (at least 2-3 alternatives)

**Step 3:** List your responses, even small or failed ones:
- What did I do?
- What did I try?
- How did I show up, even imperfectly?

**Step 4:** Rewrite in oscillating form using the structure:
- Context + Difficulty + Struggle + Response + Outcome + Meaning

Read both versions aloud. Notice how they feel different in your body. Notice which version opens possibility and which version closes it.

This is the practice. You'll likely need to do this multiple times before the oscillating version feels true. That's normal. Keep going.`,
        },
      },

      {
        id: 'oscillating-in-practice',
        title: 'Oscillating Narratives in Practice',
        duration: '6 minutes',
        content: {
          intro: `Imagine a father describing the loss of his job three different ways in one day, depending on who's asking.

At the park, talking to another parent: "Yeah, got made redundant last month. Blessing in disguise, really. Already got something lined up."

In the car, when his seven-year-old daughter asks why he's home during the day: "Daddy's job ended. Companies sometimes have to let people go. It's okay, I'm looking for something new."

At dinner, when she asks directly, "Are we going to be okay?": "I'll tell you the truth. I lost my job, which was scary. Money's going to be tight for a bit. Mum and I are worried about some things. But we've been through hard stuff before. We always figure it out. We might have to make some changes: maybe no holiday this year, maybe we eat out less. But we're going to be okay. And I want you to know: when hard things happen, grown-ups don't have to pretend everything's perfect. We just have to tell the truth and keep working on it."

Same event. Three different narrative structures. But that last one, the oscillating version, does something the others don't. It acknowledges reality without creating panic, and it shows a way through hard times without toxic positivity.

You now understand the theory and structure of oscillating narratives. This final section is about application: using them strategically in the moments that actually matter.`,

          mainContent: `**Where Oscillating Narratives Matter Most**

**1. Parenting and Family Storytelling**

This is where Marshall Duke's interest began: children, and the stories their families tell.

When you tell your children stories about your own life (or their grandparents' lives, or family history), the pattern you use may become one of the templates they use for understanding difficulty.

**Descending family narrative:**
"Your grandfather worked himself to death at a job that didn't appreciate him. He sacrificed everything and got nothing in return."

**Ascending family narrative:**
"Your grandfather worked hard and everything always worked out great for him!"

**Oscillating family narrative:**
"Your grandfather worked incredibly hard at a job that didn't always treat him well. There were times he felt unappreciated and exhausted. But he showed up every day because he was providing for his family. He taught me that work isn't always fulfilling, but you can still take pride in reliability. I'm still figuring out my own relationship with work, but that's part of what I learnt from him."

The oscillating version gives your child a framework: difficulty happens, people respond, meaning emerges.

[RESEARCH: The "Do You Know?" Scale in Practice | Duke, Lazarus and Fivush (2008) asked children 20 yes-or-no questions about family history, such as: "Do you know where some of your grandparents grew up?" "Do you know some of the lessons that your parents learned from good or bad experiences?" "Do you know how your parents met?" Children who knew more of the answers tended to have higher self-esteem and lower anxiety. The study didn't sort the stories by pattern, so it can't tell us whether oscillating ones matter most. Duke and Fivush call what these children have a strong "intergenerational self": a sense of belonging to people who have faced hard things and found ways forward.]

**2. Professional Contexts (Interviews, Leadership, Networking)**

Oscillating narratives are compelling in professional settings because they demonstrate:
- Self-awareness (you can reflect on difficulty)
- Resilience (you respond to setbacks)
- Willingness to learn (you learn from experience)

**Interview question:** "Tell me about a time you failed."

**Descending response:**
"I launched a product that completely flopped. We lost a lot of money and credibility. It was a disaster."

**Ascending response:**
"I launched a product that didn't succeed initially, but we pivoted quickly and it became our best-seller!"

**Oscillating response:**
"I launched a product that failed. Badly. We'd misread the market and I'd ignored early warning signs because I was personally invested. It cost us money and credibility. I had to own that in front of my team, which was uncomfortable. What I learnt was to build in external feedback loops earlier, before I'm too committed. I also learnt I can recover from public failure. The next product I launched, I approached differently: more testing, less ego. It didn't become a best-seller, but it was solid and sustainable."

The oscillating version shows honesty, accountability, learning and the ability to recover, which is what that interview question is usually probing for.

**3. Personal Relationships (Vulnerability and Connection)**

Oscillating narratives can help build closeness because they're honest without being overwhelming.

Descending narratives can feel like dumping: "My childhood was awful and I'm a mess."

Ascending narratives can feel like performing: "Everything's always been great!"

Oscillating narratives create space for real connection: "I had a complicated childhood. Some parts were genuinely hard. I'm still working through how that shaped me. But I'm learning what healthy relationships look like, even when it's uncomfortable."

Arthur Aron and colleagues (1997) found that strangers who took turns answering gradually deepening questions for 45 minutes felt closer than pairs who made small talk. That's **escalating self-disclosure**: sharing progressively more personal information. Oscillating narratives allow vulnerability (the difficulty) whilst maintaining boundaries (the agency).

**4. Personal Reflection and Therapy**

Oscillating narratives can be useful in reflection and therapy. Expressive writing that turns into a story has been linked to modest health benefits (Pennebaker & Seagal, 1999; Frattaroli, 2006), and life stories with redemption and agency go with better well-being (McAdams & McLean, 2013). Neither line of research tested "oscillating" stories as such.

When you keep telling yourself the oscillating version (in your journal, in therapy, in your own head), you practise a new way of holding the memory. Claims that retelling "reconsolidates" memories in the brain go beyond the evidence: in humans the idea is still hotly contested (Elsey, van Ast & Kindt, 2018).

**5. Public Speaking and Storytelling**

When you're telling a story on stage, to a group, or in writing, oscillating narratives:
- Feel authentic (not performatively positive)
- Build trust (you're not hiding difficulty)
- Create identification (listeners see their own struggles reflected)
- Inspire without manipulation (you're showing real resilience, not fake resolution)

Read this way, some well-known talks move between difficulty and meaning much like an oscillating narrative: Brené Brown's TED talk on vulnerability, for example, or Chimamanda Ngozi Adichie's on the danger of a single story. That's our reading, not how the speakers describe their talks.

[INSIGHT: The Pratfall Effect and Its Limits | The "pratfall effect" comes from **Elliot Aronson, Ben Willerman and Joanne Floyd (1966)**. Participants heard a tape of a quiz contestant who was either highly competent or average; on some tapes he then spilled his coffee. **The nuance most retellings drop is the one that matters here:** the blunder made the *highly competent* contestant more likeable and the *average* one less likeable, and later studies found the effect also depends on who is listening (Helmreich, Aronson & LeFan, 1970). Which is a real constraint on the advice. Our reading for storytelling: admitting difficulty may not warm people to you on its own; it's more likely to once they can see you're capable. That's precisely the shape of an oscillating narrative: the struggle and the response arrive together, so the difficulty reads as human rather than as the whole story.]

**The Oscillating Mindset: Beyond Individual Stories**

Once you understand oscillating narratives, you start noticing them everywhere: in culture, politics, media, relationships.

Some groups (nations, companies, families) default to ascending narratives: "We've always been the best, and we always will be."

Some default to descending: "Everything's falling apart, we're doomed."

Oscillating cultures say: "We've had moments of great achievement and moments of profound failure. We're still figuring out who we want to be."

This isn't just about individual stories. It's about how entire communities make sense of their past and imagine their future.

**Practice: The Weekly Oscillating Narrative**

At the end of each week, write a brief oscillating narrative about your week:

- What was hard this week?
- How did I respond (even imperfectly)?
- What am I learning?

This practice does two things:
1. It trains you to notice your agency, even in difficult weeks
2. It prevents descending narratives from calcifying ("I always struggle with this, nothing changes")

Over time, you'll build a record of oscillating narratives: evidence that you respond to difficulty, that you keep learning, that you're more resilient than you think.`,

          practicalApplication: `**Choosing Your Narrative Pattern Strategically**

Not every story needs to be oscillating. Sometimes a simple ascending story is fine ("I tried a new recipe and it turned out great!"). Sometimes a descending story is just venting ("That meeting was a waste of time").

But the stories that *shape your identity* are worth constructing as oscillating narratives: the ones you tell repeatedly about yourself, the ones you pass on to others, the ones that define how you see your life.

This week:
1. **Notice** which of your recurring stories are descending, ascending, or oscillating
2. **Choose** one descending story that matters to you
3. **Rewrite** it in oscillating form
4. **Tell** the oscillating version to someone you trust
5. **Reflect** on how it felt different to tell it that way

You're not erasing the difficulty. You're adding the rest of the story: your agency, your resilience, your ongoing learning.

That's the practice. That's the framework. That's how you tell a hard story whole.`,
        },
      },
    ],

    exercises: [
      {
        id: 'narrative-pattern-audit',
        title: 'Your Narrative Pattern Audit',
        type: 'Reflection',
        duration: '20 minutes',
        description: 'Identify your default narrative pattern and its impact on your life.',
        instructions: [
          'Write down 3-5 stories you tell about yourself regularly (to friends, in interviews, in your own head)',
          'For each story, identify: Is this ascending, descending, or oscillating?',
          'Notice: Do you have a default pattern? Do most of your stories follow one structure?',
          'Choose one descending narrative',
          'Rewrite it in oscillating form using the 6-part structure: Context, Difficulty, Struggle, Response, Outcome, Meaning',
          'Reflect: How does the story feel different when told this way? What changes when you add your agency alongside the difficulty?',
        ],
      },
      {
        id: 'family-narrative-mapping',
        title: 'Family Narrative Mapping',
        type: 'Practice',
        duration: '30 minutes',
        description: 'Map the narrative patterns you heard growing up and their impact on you.',
        instructions: [
          'Think of 3-5 stories you heard from parents or grandparents about family history',
          'Write each story briefly (3-5 sentences as you remember hearing it)',
          'Identify the pattern: ascending, descending, or oscillating?',
          'Reflect: What framework did these stories give you for understanding difficulty?',
          'If you\'re a parent: Choose one family story you want to tell your children',
          'Rewrite it in oscillating form if needed (acknowledge difficulty + response + honest meaning)',
          'Practise telling the oscillating version aloud, and notice how it feels to tell the struggle and the response together, not just success or struggle',
        ],
      },
      {
        id: 'weekly-oscillating-journal',
        title: 'Weekly Oscillating Narrative Journal',
        type: 'Ongoing Practice',
        duration: '10 minutes per week',
        description: 'Build the habit of oscillating narratives through weekly reflection.',
        instructions: [
          'At the end of each week, set aside 10 minutes',
          'Write an oscillating narrative about your week using this structure:',
          '  - What was genuinely hard this week? (don\'t minimise)',
          '  - What did I do in response? (even small actions)',
          '  - Where am I now with it? (honest, not resolved)',
          '  - What am I learning? (however small)',
          'Do this for 4-6 weeks and then read your entries',
          'Notice: You\'re building evidence of your resilience. You respond to difficulty. You keep learning. Week after week.',
        ],
      },
    ],

    keyTakeaways: [
      'Marshall Duke judged the oscillating family story (ups and downs, and we stuck together) the most healthful of three patterns. The measured evidence links children\'s knowledge of family history with higher self-esteem, sense of control and lower anxiety (Duke, Lazarus & Fivush, 2008)',
      'The three narrative patterns, ascending (everything gets better), descending (everything gets worse) and oscillating (ups and downs), are different ways of making sense of difficulty and of your capacity to respond to it',
      'Oscillating narratives integrate difficulty without being defined by it. They say "this was hard AND I responded" rather than "but", which minimises struggle',
      'Transforming descending narratives into oscillating ones isn\'t about positive thinking. It\'s about locating your agency (response) alongside genuine difficulty',
      'The language of oscillating narratives uses "AND" not "BUT", holding both struggle and strength as equally true rather than letting one override the other',
    ],

    reflectionQuestions: [
      'What narrative pattern did you hear most growing up: ascending, descending or oscillating? How has that shaped your default way of telling stories about yourself?',
      'Think of a story you\'ve been telling in a descending pattern. What small responses or moments of agency have you been editing out of that story?',
      'When you rewrite a descending narrative into an oscillating one, does it feel like you\'re being honest or like you\'re minimising the difficulty? What makes the difference?',
      'If you\'re a parent, what narrative pattern do you want to pass on to your children? What family stories could you tell in oscillating form, so they hear both the hard times and how your family got through them?',
      'What would change in your life if you consistently told oscillating narratives about difficulty rather than descending or ascending ones?',
    ],

    nextSteps: `You've learnt a narrative structure that holds both struggle and strength: the oscillating narrative.

**Module 4: Story Structure That Works** shows you a core structure for personal stories and how to adapt it for different audiences and situations. You'll learn when to tell ascending, descending, or oscillating narratives strategically.

**Toolkit Suggestion:** Use the **Oscillating Narrative Builder** to practise transforming descending stories into oscillating ones with guided prompts and structure.`,
  },
};

export default storyModule03Content;
